import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  DashboardCard,
  DashboardContainer,
  QuickActionItem,
  RoleContentContainer,
} from '../components/styled/DashboardComponents';
import {
  TrendingUp,
  DollarSign,
  ShoppingCart,
  Package,
  FileText,
  BarChart3,
  Users,
  AlertCircle,
  RefreshCw,
  Settings,
  HardDrive,
  Loader2,
} from 'lucide-react';

import Layout from '../components/Layout';
import dashboardService from '../services/dashboardService';
import authService from '../services/authService';
import DeliveryAlertsCard from '../components/DeliveryAlertsCard';

const DashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dashboardData, setDashboardData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  const currentUser = authService.getCurrentUser();
  const userRole = currentUser?.role || 'sales_person';

  // Load dashboard data
  const loadDashboardData = React.useCallback(async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      setError('');

      const [roleBasedData, userAlerts] = await Promise.all([
        dashboardService.getRoleBasedMetrics(userRole),
        dashboardService.getAlerts(userRole)
      ]);

      setDashboardData(roleBasedData);
      setAlerts(userAlerts);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('加载仪表盘数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [userRole]);

  useEffect(() => {
    loadDashboardData();

    // Auto-refresh every 5 minutes
    const interval = setInterval(() => {
      loadDashboardData(false);
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [userRole, loadDashboardData]);

  // Icon mapping
  const iconMap = {
    money: DollarSign,
    sales: ShoppingCart,
    stock: Package,
    quotation: FileText,
    people: Users,
    storage: HardDrive,
    reports: BarChart3,
    settings: Settings,
    inventory: Package,
  };

  // Get role-specific quick actions
  const getQuickActions = () => {
    const baseActions = [
      { label: '创建报价', iconKey: 'quotation', path: '/quotations/new' },
      { label: '查看报表', iconKey: 'reports', path: '/reports' }
    ];

    switch (userRole) {
      case 'admin':
        return [
          ...baseActions,
          { label: '用户管理', iconKey: 'people', path: '/users' },
          { label: '系统设置', iconKey: 'settings', path: '/settings' }
        ];
      case 'warehouse_manager':
        return [
          { label: '库存管理', iconKey: 'inventory', path: '/inventory' },
          { label: '添加产品', iconKey: 'inventory', path: '/products/new' },
          ...baseActions
        ];
      case 'sales_person':
        return [
          ...baseActions,
          { label: '新建销售', iconKey: 'sales', path: '/sales/new' },
          { label: '客户报价', iconKey: 'quotation', path: '/quotations' }
        ];
      default:
        return baseActions;
    }
  };

  // Render role-specific content
  const renderRoleSpecificContent = () => {
    if (!dashboardData) return null;

    switch (userRole) {
      case 'admin':
        return renderAdminContent();
      case 'warehouse_manager':
        return renderWarehouseContent();
      case 'sales_person':
        return renderSalesContent();
      default:
        return renderDefaultContent();
    }
  };

  const renderAdminContent = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <DashboardCard>
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-4 text-foreground">系统概览</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">系统运行时间</span>
              <span className="font-medium">{dashboardData.systemOverview?.systemUptime || '99.9%'}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">活跃用户</span>
              <span className="font-medium">{dashboardData.systemOverview?.activeUsers || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">存储使用</span>
              <span className="font-medium">{dashboardData.systemOverview?.storageUsed || '45%'}</span>
            </div>
          </div>
        </CardContent>
      </DashboardCard>
      <DashboardCard>
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-4 text-foreground">业务指标</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">总收入</span>
              <span className="font-medium">{dashboardService.formatCurrency(dashboardData.businessMetrics?.totalRevenue || 0)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">利润率</span>
              <span className="font-medium text-success">{dashboardService.formatPercentage(dashboardData.businessMetrics?.profitMargin || 0)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">转换率</span>
              <span className="font-medium">{dashboardService.formatPercentage(dashboardData.businessMetrics?.conversionRate || 0)}</span>
            </div>
          </div>
        </CardContent>
      </DashboardCard>
    </div>
  );

  const renderWarehouseContent = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="md:col-span-2">
        <DashboardCard>
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold mb-4 text-foreground">库存警报</h3>
            {dashboardData.inventoryAlerts?.criticalStock?.length > 0 ? (
              <div className="space-y-3">
                {dashboardData.inventoryAlerts.criticalStock.slice(0, 5).map((item, index) => (
                  <div key={index} className="flex items-center gap-3 py-2">
                    <AlertCircle className="h-5 w-5 text-destructive shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{item.name || `商品 ${item.id}`}</p>
                      <p className="text-sm text-muted-foreground">剩余数量: {item.quantity || 0}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">暂无关键库存警报</p>
            )}
          </CardContent>
        </DashboardCard>
      </div>
      <DashboardCard>
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-4 text-foreground">仓库效率</h3>
          <div className="space-y-6">
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-muted-foreground">拣货效率</span>
                <span className="text-sm font-medium">{dashboardData.warehouseOperations?.pickingEfficiency || 85}%</span>
              </div>
              <Progress value={dashboardData.warehouseOperations?.pickingEfficiency || 85} className="h-2" />
            </div>
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-muted-foreground">仓储利用率</span>
                <span className="text-sm font-medium">{dashboardData.warehouseOperations?.storageUtilization || 75}%</span>
              </div>
              <Progress value={dashboardData.warehouseOperations?.storageUtilization || 75} className="h-2" />
            </div>
          </div>
        </CardContent>
      </DashboardCard>
    </div>
  );

  const renderSalesContent = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="md:col-span-2">
        <DashboardCard>
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold mb-4 text-foreground">销售管道</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-3xl font-bold text-primary">{dashboardData.salesPipeline?.quotationsInProgress || 0}</p>
                <p className="text-sm text-muted-foreground">进行中</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-warning">{dashboardData.salesPipeline?.quotationsPending || 0}</p>
                <p className="text-sm text-muted-foreground">待处理</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-success">{dashboardData.salesPipeline?.quotationsConverted || 0}</p>
                <p className="text-sm text-muted-foreground">已转换</p>
              </div>
            </div>
          </CardContent>
        </DashboardCard>
      </div>
      <DashboardCard>
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-4 text-foreground">销售目标</h3>
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-muted-foreground">本月目标</span>
              <span className="text-sm font-medium">
                {Math.round(((dashboardData.salesTargets?.monthlyActual || 0) / (dashboardData.salesTargets?.monthlyTarget || 1)) * 100)}%
              </span>
            </div>
            <Progress
              value={Math.min(100, ((dashboardData.salesTargets?.monthlyActual || 0) / (dashboardData.salesTargets?.monthlyTarget || 1)) * 100)}
              className="h-2 mb-2"
            />
            <p className="text-xs text-muted-foreground">
              {dashboardService.formatCurrency(dashboardData.salesTargets?.monthlyActual || 0)} / {dashboardService.formatCurrency(dashboardData.salesTargets?.monthlyTarget || 0)}
            </p>
          </div>
        </CardContent>
      </DashboardCard>
    </div>
  );

  const renderDefaultContent = () => (
    <DashboardCard>
      <CardContent className="p-6">
        <h3 className="text-lg font-semibold mb-4 text-foreground">最近报价</h3>
        {dashboardData.customerInteractions?.recentQuotations?.length > 0 ? (
          <div className="divide-y">
            {dashboardData.customerInteractions.recentQuotations.slice(0, 5).map((quotation, index) => (
              <div key={quotation._id || index} className="flex justify-between items-center py-3">
                <div>
                  <p className="font-medium">{quotation.quotation_id || quotation._id}</p>
                  <p className="text-sm text-muted-foreground">{quotation.customer_name || '未知客户'}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-semibold">{dashboardService.formatCurrency(quotation.total_amount || 0)}</span>
                  <Badge variant={quotation.status === 'pending' ? 'warning' : quotation.status === 'confirmed' ? 'secondary' : 'success'}>
                    {quotation.status === 'pending' ? '待处理' : quotation.status === 'confirmed' ? '已确认' : '已转换'}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-4">暂无最近报价记录</p>
        )}
      </CardContent>
    </DashboardCard>
  );

  if (loading) {
    return (
      <Layout title="仪表盘" activeTab="dashboard">
        <DashboardContainer>
          <div className="flex justify-center items-center min-h-[60vh]">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
          </div>
        </DashboardContainer>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout title="仪表盘" activeTab="dashboard">
        <DashboardContainer>
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </DashboardContainer>
      </Layout>
    );
  }

  const quickActions = getQuickActions();

  return (
    <TooltipProvider>
      <Layout title="仪表盘" activeTab="dashboard">
        <DashboardContainer>
          {/* Header with refresh button */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-1">
                欢迎回来，{currentUser?.display_name || currentUser?.username || '用户'}
              </h1>
              <p className="text-muted-foreground">
                {userRole === 'admin' ? '系统管理员视图' :
                  userRole === 'warehouse_manager' ? '仓库管理员视图' :
                    userRole === 'sales_person' ? '销售人员视图' : '标准视图'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">
                更新时间: {lastUpdated.toLocaleTimeString()}
              </span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" onClick={() => loadDashboardData(false)}>
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>刷新数据</TooltipContent>
              </Tooltip>
            </div>
          </div>

          {/* Alerts */}
          {alerts.length > 0 && (
            <div className="space-y-2 mb-8">
              {alerts.map((alert, index) => (
                <Alert key={index} variant={alert.type === 'error' ? 'destructive' : 'default'} className="flex items-center justify-between">
                  <div>
                    <AlertTitle className="font-medium">{alert.title}</AlertTitle>
                    <AlertDescription>{alert.message}</AlertDescription>
                  </div>
                  {alert.action && (
                    <Button variant="ghost" size="icon" onClick={() => navigate(alert.action)}>
                      <TrendingUp className="h-4 w-4" />
                    </Button>
                  )}
                </Alert>
              ))}
            </div>
          )}

          <DeliveryAlertsCard />

          {/* Role-specific content */}
          <RoleContentContainer>
            {renderRoleSpecificContent()}
          </RoleContentContainer>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <DashboardCard>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold mb-4 text-foreground">快速操作</h3>
                <div className="space-y-2">
                  {quickActions.map((action, index) => {
                    const IconComponent = iconMap[action.iconKey] || Package;
                    return (
                      <QuickActionItem
                        key={index}
                        onClick={() => action.path && navigate(action.path)}
                      >
                        <IconComponent className="h-5 w-5 text-primary mr-3" />
                        <span className="font-medium">{action.label}</span>
                      </QuickActionItem>
                    );
                  })}
                </div>
              </CardContent>
            </DashboardCard>
          </div>
        </DashboardContainer>
      </Layout>
    </TooltipProvider>
  );
};

export default DashboardPage;
